/**
 * Ритм (F6 музыка, G6 ночь, G11 конвейер): «Начать» запускает отсчёт,
 * большая кнопка «Тап» пишет время от старта в мс; такты показываются
 * текстом «Такт n из N» в момент удара (без анимации, при «меньше
 * движения» ничего не меняется — текст и так статичен). По истечении
 * `durationMs` или по «Закончить» отдаём все тапы. Точность считает
 * логика, экран только собирает времена.
 */
import { t } from '@life/i18n';
import { useEffect, useRef, useState } from 'preact/hooks';

export interface TapProps {
  /** Моменты ударов от старта, мс. */
  beats: readonly number[];
  durationMs: number;
  onDone: (taps: number[]) => void;
  /** Подпись кнопки (по умолчанию «Тап»). */
  tapLabel?: string;
}

export function Tap({ beats, durationMs, onDone, tapLabel }: TapProps) {
  const [started, setStarted] = useState<number | null>(null);
  const [beat, setBeat] = useState(0);
  const [count, setCount] = useState(0);
  const taps = useRef<number[]>([]);
  const timers = useRef<ReturnType<typeof setTimeout>[]>([]);
  const finished = useRef(false);

  const clear = () => {
    for (const id of timers.current) clearTimeout(id);
    timers.current = [];
  };

  const finish = () => {
    if (finished.current) return;
    finished.current = true;
    clear();
    onDone([...taps.current]);
  };

  useEffect(() => clear, []);

  const start = () => {
    if (started !== null) return;
    const at = Date.now();
    setStarted(at);
    beats.forEach((b, i) => {
      timers.current.push(setTimeout(() => setBeat(i + 1), b));
    });
    timers.current.push(setTimeout(finish, durationMs));
  };

  const tap = () => {
    if (started === null || finished.current) return;
    taps.current.push(Date.now() - started);
    setCount(taps.current.length);
  };

  if (started === null) {
    return (
      <button type="button" class="btn accent block" onClick={start}>
        {t('ui.minigame.tap.start')}
      </button>
    );
  }

  return (
    <div class="tap">
      <p class="hint mono" aria-live="polite">
        {t('ui.minigame.tap.beat', { n: beat, total: beats.length })} · {t('ui.minigame.tap.count', { n: count })}
      </p>
      <button
        type="button"
        class="btn accent tap-btn"
        onPointerDown={(e) => {
          e.preventDefault();
          tap();
        }}
        onKeyDown={(e) => {
          if (e.key === ' ' || e.key === 'Enter') {
            e.preventDefault();
            tap();
          }
        }}
      >
        {tapLabel ?? t('ui.minigame.tap.tap')}
      </button>
      <button type="button" class="btn ghost" onClick={finish}>
        {t('ui.minigame.tap.stop')}
      </button>
    </div>
  );
}
